import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaClock, FaPassport, FaFileDownload, FaArrowLeft, FaCheckCircle, FaMoneyBillWave, FaGlobe, FaCalendarAlt } from "react-icons/fa";
import { motion } from "framer-motion";
import PageWrapper from "../components/PageWrapper";
import { supabase } from "../lib/supabase";
import { useCart } from "../context/CartContext";
import TravelerDetailsModal from "../components/TravelerDetailsModal";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle, visaSchema, breadcrumbSchema } from "../lib/seo";
import "../styles/DetailPage.css";

function VisaDetailNew() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [visa, setVisa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchVisaDetails();
    // eslint-disable-next-line
  }, [id]);

  async function fetchVisaDetails() {
    try {
      const { data, error } = await supabase
        .from("visas")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      setVisa(data);
    } catch (error) {
      console.error("Error fetching visa:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleApply() {
    setShowModal(true);
  }

  function handleModalSubmit(travelerDetails) {
    if (!visa) return;
    
    const cartItem = {
      id: visa.id,
      name: `${visa.country} Visa`,
      type: 'visa',
      price: formatPrice(visa.price),
      image: visa.image,
      destination: visa.country,
      country: visa.country,
    };
    
    addToCart(cartItem, travelerDetails);
    setShowModal(false);
    navigate("/cart");
  }

  function formatPrice(price) {
    return `₹${Number(price).toLocaleString("en-IN")}`;
  }

  usePageMeta({
    title: visa ? pageTitle(`${visa.country} ${visa.visa_type || "Tourist"} Visa for Indians`) : undefined,
    description: visa ? `Apply for your ${visa.country} ${(visa.visa_type || "tourist").toLowerCase()} visa from India. Fee ${formatPrice(visa.price)}${visa.processing_time ? `, processing time ${visa.processing_time}` : ""}. See the documents required.` : undefined,
    image: visa?.image,
    jsonLd: visa ? [
      visaSchema(visa),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Visa Services", path: "/visa" },
        { name: `${visa.country} Visa`, path: `/visa/${visa.id}` },
      ]),
    ] : undefined,
  });

  if (loading) {
    return (
      <PageWrapper>
        <div style={{ padding: "100px 20px", textAlign: "center" }}>
          <div className="loading-spinner"></div>
          <p>Loading visa details...</p>
        </div>
      </PageWrapper>
    );
  }

  if (!visa) {
    return (
      <PageWrapper>
        <div style={{ padding: "100px 20px", textAlign: "center" }}>
          <h2>Visa not found</h2>
          <button onClick={() => navigate("/visa/")} className="btn-primary">
            Back to Visa Services
          </button>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="detail-page visa-detail-page">
        {/* Enhanced Hero Section */}
        <div className="visa-hero-section" style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6)), url(${visa.image || "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1600"})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '500px',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          borderRadius: '0 0 32px 32px',
          marginBottom: '40px'
        }}>
          <button 
            onClick={() => navigate("/visa/")} 
            className="back-button-floating"
            style={{
              position: 'absolute',
              top: '24px',
              left: '24px',
              background: 'rgba(255, 255, 255, 0.95)',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '50px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              transition: 'all 0.3s ease'
            }}
          >
            <FaArrowLeft /> Back
          </button>

          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', width: '100%' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              style={{ color: 'white', textAlign: 'center' }}
            >
              <div style={{
                display: 'inline-block',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                padding: '8px 20px',
                borderRadius: '50px',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                <FaPassport style={{ marginRight: '8px' }} />
                {visa.visa_type || "Tourist Visa"}
              </div>
              
              <h1 style={{
                fontSize: '56px',
                fontWeight: '800',
                margin: '0 0 16px 0',
                textShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }}>
                {visa.country} Visa
              </h1>
              
              <p style={{
                fontSize: '20px',
                opacity: '0.95',
                maxWidth: '600px',
                margin: '0 auto 32px',
                lineHeight: '1.6'
              }}>
                Get your {visa.country} visa hassle-free with complete assistance and documentation support
              </p>

              <div style={{
                display: 'flex',
                gap: '24px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  padding: '16px 24px',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <FaClock style={{ fontSize: '24px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '14px', opacity: '0.9' }}>Processing Time</div>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>{visa.processing_time || "5-7 days"}</div>
                </div>
                
                <div style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  padding: '16px 24px',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <FaMoneyBillWave style={{ fontSize: '24px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '14px', opacity: '0.9' }}>Visa Fee</div>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>{formatPrice(visa.price)}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Main Content */}
        <div className="detail-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 80px' }}>
          <div className="detail-grid">
            
            {/* Left Column - Main Info */}
            <div>
              
              {/* Quick Info Cards */}
              <div className="visa-info-grid">
                <div className="info-highlight-card">
                  <FaGlobe style={{ fontSize: '28px', color: '#0ea5e9', marginBottom: '12px' }} />
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>Country</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>{visa.country}</div>
                </div>
                
                <div className="info-highlight-card">
                  <FaPassport style={{ fontSize: '28px', color: '#10b981', marginBottom: '12px' }} />
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>Visa Type</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>{visa.visa_type || "Tourist"}</div>
                </div>
                
                <div className="info-highlight-card">
                  <FaCalendarAlt style={{ fontSize: '28px', color: '#f59e0b', marginBottom: '12px' }} />
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase' }}>Processing</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>{visa.processing_time || "5-7 days"}</div>
                </div>
              </div>

              {/* Requirements Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="detail-section"
                style={{
                  background: 'white',
                  borderRadius: '24px',
                  padding: '32px',
                  marginBottom: '32px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  border: '1px solid #e2e8f0'
                }}
              >
                <h2 style={{ 
                  fontSize: '28px', 
                  fontWeight: '700', 
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px'
                  }}>
                    <FaCheckCircle />
                  </div>
                  Visa Requirements
                </h2>
                
                <div style={{ display: 'grid', gap: '12px', marginBottom: '32px' }}>
                  {visa.requirements && visa.requirements.length > 0 ? (
                    visa.requirements.map((req, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          padding: '18px 20px',
                          background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                          borderRadius: '12px',
                          border: '2px solid #d1fae5',
                          transition: 'all 0.3s ease'
                        }}
                        whileHover={{ scale: 1.02, boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)' }}
                      >
                        <FaCheckCircle style={{ color: '#10b981', fontSize: '22px', flexShrink: 0 }} />
                        <span style={{ fontSize: '16px', color: '#1e293b', fontWeight: '500' }}>{req}</span>
                      </motion.div>
                    ))
                  ) : (
                    <p style={{ color: '#64748b', fontSize: '15px', padding: '20px', textAlign: 'center', background: '#f8fafc', borderRadius: '12px' }}>
                      No specific requirements listed. Contact us for details.
                    </p>
                  )}
                </div>

                {/* PDF DOWNLOADS - ALWAYS VISIBLE SECTION */}
                <div className="visa-pdf-section" style={{
                  borderTop: '2px dashed #e2e8f0',
                  paddingTop: '32px',
                  marginTop: '24px'
                }}>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    color: '#1e293b'
                  }}>
                    <FaFileDownload style={{ color: '#0ea5e9', fontSize: '24px' }} />
                    Download Required Documents
                  </h3>

                  <div style={{ display: 'grid', gap: '16px' }}>
                    {/* Requirements PDF #1 */}
                    {visa.requirements_pdf_1 ? (
                      <motion.a
                        href={visa.requirements_pdf_1}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.02, y: -4 }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '20px',
                          padding: '24px',
                          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                          borderRadius: '16px',
                          textDecoration: 'none',
                          border: '3px solid #3b82f6',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
                        }}
                      >
                        <div style={{
                          width: '64px',
                          height: '64px',
                          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                          borderRadius: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px',
                          color: 'white',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)'
                        }}>
                          📄
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
                            📥 Requirements Document #1
                          </h4>
                          <p style={{ margin: 0, fontSize: '14px', color: '#64748b', lineHeight: '1.5' }}>
                            Main visa requirements and guidelines
                          </p>
                        </div>
                        <div style={{
                          padding: '12px 24px',
                          background: '#0ea5e9',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '700',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 8px rgba(14, 165, 233, 0.3)'
                        }}>
                          DOWNLOAD →
                        </div>
                      </motion.a>
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                        padding: '24px',
                        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                        borderRadius: '16px',
                        border: '3px dashed #cbd5e1'
                      }}>
                        <div style={{
                          width: '64px',
                          height: '64px',
                          background: '#94a3b8',
                          borderRadius: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px',
                          color: 'white',
                          flexShrink: 0
                        }}>
                          📄
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '700', color: '#64748b' }}>
                            Requirements Document #1
                          </h4>
                          <p style={{ margin: 0, fontSize: '14px', color: '#94a3b8' }}>
                            Not available - Contact us for requirements document
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Requirements PDF #2 */}
                    {visa.requirements_pdf_2 ? (
                      <motion.a
                        href={visa.requirements_pdf_2}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.02, y: -4 }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '20px',
                          padding: '24px',
                          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                          borderRadius: '16px',
                          textDecoration: 'none',
                          border: '3px solid #0284c7',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(2, 132, 199, 0.15)'
                        }}
                      >
                        <div style={{
                          width: '64px',
                          height: '64px',
                          background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                          borderRadius: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px',
                          color: 'white',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(2, 132, 199, 0.3)'
                        }}>
                          📄
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
                            📥 Requirements Document #2
                          </h4>
                          <p style={{ margin: 0, fontSize: '14px', color: '#64748b', lineHeight: '1.5' }}>
                            Additional requirements and documentation
                          </p>
                        </div>
                        <div style={{
                          padding: '12px 24px',
                          background: '#0284c7',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '700',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 8px rgba(2, 132, 199, 0.3)'
                        }}>
                          DOWNLOAD →
                        </div>
                      </motion.a>
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                        padding: '24px',
                        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                        borderRadius: '16px',
                        border: '3px dashed #cbd5e1'
                      }}>
                        <div style={{
                          width: '64px',
                          height: '64px',
                          background: '#94a3b8',
                          borderRadius: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px',
                          color: 'white',
                          flexShrink: 0
                        }}>
                          📄
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '700', color: '#64748b' }}>
                            Requirements Document #2
                          </h4>
                          <p style={{ margin: 0, fontSize: '14px', color: '#94a3b8' }}>
                            Not available - Contact us for additional documentation
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Application Form PDF */}
                    {visa.application_form_pdf ? (
                      <motion.a
                        href={visa.application_form_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.02, y: -4 }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '20px',
                          padding: '24px',
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          borderRadius: '16px',
                          textDecoration: 'none',
                          border: '3px solid #f59e0b',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.15)'
                        }}
                      >
                        <div style={{
                          width: '64px',
                          height: '64px',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          borderRadius: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px',
                          color: 'white',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                        }}>
                          📋
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
                            📥 Application Form PDF
                          </h4>
                          <p style={{ margin: 0, fontSize: '14px', color: '#64748b', lineHeight: '1.5' }}>
                            Downloadable visa application form (Fill & submit)
                          </p>
                        </div>
                        <div style={{
                          padding: '12px 24px',
                          background: '#f59e0b',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '700',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                        }}>
                          DOWNLOAD →
                        </div>
                      </motion.a>
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                        padding: '24px',
                        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                        borderRadius: '16px',
                        border: '3px dashed #cbd5e1'
                      }}>
                        <div style={{
                          width: '64px',
                          height: '64px',
                          background: '#94a3b8',
                          borderRadius: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px',
                          color: 'white',
                          flexShrink: 0
                        }}>
                          📋
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '700', color: '#64748b' }}>
                            Application Form PDF
                          </h4>
                          <p style={{ margin: 0, fontSize: '14px', color: '#94a3b8' }}>
                            Not available - Contact us for application form
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

            </div>

            {/* Right Column - Sticky Booking Card */}
            <div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                style={{
                  position: 'sticky',
                  top: '100px',
                  background: 'white',
                  borderRadius: '24px',
                  padding: '32px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  border: '1px solid #e2e8f0'
                }}
              >
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Visa Fee
                  </div>
                  <div style={{ fontSize: '42px', fontWeight: '800', color: '#1e293b', lineHeight: '1' }}>
                    {formatPrice(visa.price)}
                  </div>
                </div>

                <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '16px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <FaClock style={{ color: '#0ea5e9', fontSize: '18px' }} />
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Processing Time</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>{visa.processing_time || "5-7 days"}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FaPassport style={{ color: '#10b981', fontSize: '18px' }} />
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Visa Type</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>{visa.visa_type || "Tourist"}</div>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={handleApply}
                  style={{
                    width: '100%',
                    padding: '18px',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    marginBottom: '20px',
                    boxShadow: '0 4px 16px rgba(14, 165, 233, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 24px rgba(14, 165, 233, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 16px rgba(14, 165, 233, 0.3)';
                  }}
                >
                  Apply Now
                </button>

                <div style={{ paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#10b981', fontSize: '14px' }}>
                    <FaCheckCircle />
                    <span>Complete assistance provided</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981', fontSize: '14px' }}>
                    <FaCheckCircle />
                    <span>Document verification included</span>
                  </div>
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </div>

      {/* Traveler Details Modal */}
      <TravelerDetailsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleModalSubmit}
        itemName={visa ? `${visa.country} Visa` : ""}
      />
    </PageWrapper>
  );
}

export default VisaDetailNew;
